import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { LeagueIterationMemberService } from '../../../simulator-booking/services/league-iteration-member.service';

export interface PaymentDetails {
    amount?: string;
    date?: string;
    errorMessage?: string;
}

@Component({
    selector: 'payment-success-failure',
    templateUrl: './payment-success-failure.component.html',
    styleUrls: ['./payment-success-failure.component.scss'],
    standalone: false
})
export class PaymentSuccessFailureComponent implements OnInit {
    isSuccess: boolean = false;
    paymentDetails: PaymentDetails | null = null;
    showReceiptButton: boolean = false;
    continueRoute: string = '/';
    receiptRoute?: string;
    retryRoute?: string;
    isLoading: boolean = true;
    sessionId?: string;
    apiKey?: string;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private leagueIterationMemberService: LeagueIterationMemberService
    ) { }

    ngOnInit(): void {
        this.route.queryParams.subscribe(params => {
            this.sessionId = params['session_id'];
            this.apiKey = params['apiKey'];

            if (this.sessionId && this.apiKey) {
                this.fetchPaymentDetails();
            } else {
                this.isLoading = false;
                this.isSuccess = false;
                this.paymentDetails = {
                    errorMessage: 'Missing required parameters. Please check your payment link.'
                };
            }
        });
    }

    fetchPaymentDetails(): void {
        if (!this.sessionId || !this.apiKey) {
            return;
        }

        this.isLoading = true;
        this.leagueIterationMemberService.getCheckoutSession(this.apiKey, this.sessionId).subscribe({
            next: (result: any) => {
                this.isLoading = false;
                
                // Determine success based on session status
                this.isSuccess = result.payment_status === 'paid' || result.status === 'complete';
                
                // Extract payment details from the session
                this.paymentDetails = {
                    amount: this.formatAmount(result.amount_total, result.currency),
                    date: result.created ? new Date(result.created * 1000).toLocaleDateString() : new Date().toLocaleDateString(),
                    errorMessage: result.payment_status === 'unpaid' ? 'Payment was not completed.' : undefined
                };

                // Set receipt route if available
                if (this.isSuccess && result.invoice) {
                    this.receiptRoute = result.invoice.hosted_invoice_url || result.invoice.invoice_pdf;
                    this.showReceiptButton = !!this.receiptRoute;
                }

                // Set continue route based on success metadata or default
                if (result.metadata?.continueRoute) {
                    this.continueRoute = result.metadata.continueRoute;
                } else if (result.metadata?.retryRoute && !this.isSuccess) {
                    this.retryRoute = result.metadata.retryRoute;
                }
            },
            error: (error: any) => {
                this.isLoading = false;
                this.isSuccess = false;
                this.paymentDetails = {
                    errorMessage: error.error?.message || 'Unable to retrieve payment details. Please contact support if you believe this is an error.'
                };
                console.error('Error fetching checkout session:', error);
            }
        });
    }

    formatAmount(amount: number, currency: string = 'usd'): string {
        if (!amount) return '';
        const formattedAmount = (amount / 100).toFixed(2);
        const currencySymbol = currency === 'usd' ? '$' : '';
        return `${currencySymbol}${formattedAmount}`;
    }

    getTitle(): string {
        return this.isSuccess ? 'Payment Successful!' : 'Payment Failed';
    }

    getMessage(): string {
        if (this.isSuccess) {
            return 'Thank you for your payment. Your transaction has been processed successfully.';
        }
        return this.paymentDetails?.errorMessage || 'There was an error processing your payment. Please try again.';
    }
}
